<template>
  <div class="main-div">
    <div class="header-div !p-0">
    <button type="button" class="close-icon" aria-label="Close exposure sources" autofocus @click="closeDialog">
      <XMarkIcon class="w-5 h-5" aria-hidden="true" />
    </button>
      <div class="title-div">
        <div class="title-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="380"
            height="380"
            viewBox="0 0 480 480"
            fill="none"
            class="pattern"
            opacity="0.4"
          >
            <defs>
              <clipPath id="clip-circle">
                <rect x="190" y="190" width="240" height="240" />
              </clipPath>
            </defs>
            <g clip-path="url(#clip-circle)">
              <circle cx="240" cy="240" r="60" stroke="currentColor" />
              <circle cx="240" cy="240" r="92" stroke="currentColor" />
              <circle cx="240" cy="240" r="124" stroke="currentColor" />
              <circle cx="240" cy="240" r="156" stroke="currentColor" />
            </g>
          </svg>
          <div class="title-section mb-5">
            <p>Exposure Sources</p>

          </div>
          <div class="exposure-div">
            <div
              v-for="exposure in exposures"
              :key="exposure.id"
              class="exposure-card"
            >
              <div class="exposure-card-content">
                <div class="image-card">
                  <div class="inner-img-card">
                    <img
                      :src="getCloudflareResizedImage(exposure.logo, 48, 48)"
                      :alt="exposure.name"
                      :title="exposure.name"
                    />
                  </div>
                </div>
                <div class="exposure-info">
                  <a
                    :href="`/data-brokers?search=${encodeURIComponent(exposure.name)}`"
                    class="exposure-link"
                  >
                    <p>{{ exposure.name }}</p>
                  </a>
                  <span>
                    Last Detected:
                    {{ formatDate(exposure.last_scan) }}
                  </span>
                </div>
              </div>
              <a 
                :href="`/data-brokers?search=${encodeURIComponent(exposure.name)}`" 
                class="svg-link"
                tabindex="-1"
                aria-hidden="true"
              >
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 10.25C21 10.4489 20.921 10.6397 20.7803 10.7803C20.6397 10.921 20.4489 11 20.25 11C20.0511 11 19.8603 10.921 19.7197 10.7803C19.579 10.6397 19.5 10.4489 19.5 10.25V6.06125L13.2816 12.2806C13.1408 12.4214 12.95 12.5004 12.7509 12.5004C12.5519 12.5004 12.361 12.4214 12.2203 12.2806C12.0796 12.1399 12.0005 11.949 12.0005 11.75C12.0005 11.551 12.0796 11.3601 12.2203 11.2194L18.4387 5H14.25C14.0511 5 13.8603 4.92098 13.7197 4.78033C13.579 4.63968 13.5 4.44891 13.5 4.25C13.5 4.05109 13.579 3.86032 13.7197 3.71967C13.8603 3.57902 14.0511 3.5 14.25 3.5H20.25C20.4489 3.5 20.6397 3.57902 20.7803 3.71967C20.921 3.86032 21 4.05109 21 4.25V10.25ZM17.25 12.5C17.0511 12.5 16.8603 12.579 16.7197 12.7197C16.579 12.8603 16.5 13.0511 16.5 13.25V20H4.5V8H11.25C11.4489 8 11.6397 7.92098 11.7803 7.78033C11.921 7.63968 12 7.44891 12 7.25C12 7.05109 11.921 6.86032 11.7803 6.71967C11.6397 6.57902 11.4489 6.5 11.25 6.5H4.5C4.10218 6.5 3.72064 6.65804 3.43934 6.93934C3.15804 7.22064 3 7.60218 3 8V20C3 20.3978 3.15804 20.7794 3.43934 21.0607C3.72064 21.342 4.10218 21.5 4.5 21.5H16.5C16.8978 21.5 17.2794 21.342 17.5607 21.0607C17.842 20.7794 18 20.3978 18 20V13.25C18 13.0511 17.921 12.8603 17.7803 12.7197C17.6397 12.579 17.4489 12.5 17.25 12.5Z"
                    fill="currentcolor"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
   
  </div>
</template>

<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { formatDate } from '@privacy-bee/format/date';
import { ModalNames } from '~/types/modals';

const config = useRuntimeConfig();
const emailInput = ref('');
const { getCloudflareResizedImage } = useImageResize();

const dialogRef = getDialogRef(ModalNames.EXPOSURE_SOURCES);
const exposures = computed(() => dialogRef.value?.data?.exposures ?? []);

const closeDialog = () => {
    dialogRef.value.close();
};

const personStore = usePersonStore();
const isSendingVerificationCode = ref(false);

const sendVerificationCode = async () => {
    if (emailInput.value === '') return;
    isSendingVerificationCode.value = true;

    try {
        await personStore.sendEmailVerificationCode(emailInput.value);
        isSendingVerificationCode.value = false;
        dialogRef.value.close();
    } catch (error) {
        isSendingVerificationCode.value = false;
        return;
    }
};

</script>
<style lang="scss" scoped>
.main-div {
    @apply sm:w-[544px] w-11/12 rounded-lg bg-white mx-auto overflow-hidden relative;
}
.header-div {
    @apply flex flex-row items-start gap-4 self-stretch;
}
.title-div {
    @apply flex sm:flex-row flex-col p-6 pt-6 px-6 items-start gap-4 self-stretch w-full;
}
.title-wrapper {
    @apply flex flex-col items-start gap-1 sm:flex-1 sm:w-auto w-full relative;
}
.title-section {
    @apply self-stretch flex flex-row justify-between relative;

    p {
        @apply text-zinc-950 text-lg font-semibold leading-7;
    }
}
.close-icon {
    @apply flex justify-center items-center rounded-lg text-gray-400 cursor-pointer w-5 h-5 absolute top-7 right-4 z-10;
    @apply bg-transparent border-0 p-0;
}
.pattern {
    @apply absolute top-0 left-0 -translate-x-44 -translate-y-44 pointer-events-none;
    color: var(--hairline);
}
.image-card{
    @apply bg-zinc-100 w-12 h-12 rounded-lg border-2 border-white shadow-md;
}
.inner-img-card{
    @apply w-full h-full rounded-lg overflow-hidden border border-zinc-200;
}
.exposure-div{
    @apply w-full max-h-96 overflow-y-auto relative;
    
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
        display: none;
    }
    
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}
.exposure-card{
    @apply flex flex-row items-center gap-2 mb-4 justify-between w-full;

    .svg-link{
        @apply text-zinc-500 hover:text-blue-600 transition-colors duration-200 ease-in-out;
    }
}
.exposure-card-content{
    @apply flex flex-row items-center gap-4;

    .exposure-info {
        @apply flex flex-col gap-1.5;

        .exposure-link {
            @apply no-underline;
            
            p {
                @apply text-blue-600 text-sm font-semibold hover:underline;
            }
        }
        
        p {
            @apply text-zinc-950 text-sm font-semibold;
        }

        span {
            @apply text-zinc-700 text-xs font-normal;
        }
    }
}

</style>
